import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  CircleUser,
  ExternalLink,
  FileLineChart,
  LayoutDashboard,
  Lightbulb,
  Radar,
  Ship,
  Webhook,
} from "lucide-react";
import { type PropsWithChildren, useEffect, useState } from "react";
import { FeedbackDialog } from "@/components/feedback-dialog";
import { ThemeMenuItem } from "@/components/theme-menu-item";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Auth } from "@/lib/auth";
import { logout } from "@/lib/auth-api";
import { useFeatureFlag } from "@/lib/lib-feature-flag";
import { LibFleetTracker } from "@/lib/lib-fleet-tracker";
import { LibWebhooks } from "@/lib/lib-webhooks";

export const Route = createFileRoute("/_sidebar")({
  component: SidebarLayout,
});

function SidebarLayout() {
  return (
    <Auth.RequireAuth>
      <CustomerRouteGuard>
        <SidebarProvider>
          <div data-component="SidebarLayout" className="relative flex h-dvh w-full">
            <AppSidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              <MobileTopBar />
              <main id="app-main-content" className="relative flex-1 flex-col overflow-y-auto">
                <Outlet />
              </main>
            </div>
          </div>
        </SidebarProvider>
      </CustomerRouteGuard>
    </Auth.RequireAuth>
  );
}

// UX guard only — the real boundary is the backend collection rules
// (migration 342): customer accounts are scoped to Turrapporter (and webhook
// settings, which self-gate via customer_users). Internal routes redirect.
function CustomerRouteGuard({ children }: PropsWithChildren) {
  const auth = Auth.useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAllowedPath = location.pathname.startsWith("/mission") || location.pathname.startsWith("/settings/webhooks");
  const shouldRedirect = auth.isCustomerUser && !isAllowedPath;

  useEffect(() => {
    if (shouldRedirect) {
      navigate({ to: "/mission/overview" });
    }
  }, [shouldRedirect, navigate]);

  if (shouldRedirect) {
    return null;
  }
  return <>{children}</>;
}

function MobileTopBar() {
  return (
    <header
      id="mobile-top-bar"
      className="flex h-12 shrink-0 items-center gap-2 border-b bg-background px-2 md:hidden dark:bg-card"
    >
      <SidebarTrigger id="mobile-sidebar-trigger" className="h-9 w-9" title="Åpne meny" />
      <img src="/image38.svg" alt="Frøy logo" className="h-7 w-7 object-contain" />
      <span className="font-semibold text-base">
        Frøy <span className="font-normal">Engage</span>
      </span>
    </header>
  );
}

function AppSidebar() {
  const location = useLocation();
  const auth = Auth.useAuth();
  const flagSidebarButtonDashboard = useFeatureFlag("sidebar_button_dashboard");
  const webhookAccess = LibWebhooks.useWebhookAccess();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { setOpenMobile, isOverlay, setOpen } = useSidebar();

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on route change to close the mobile drawer after navigation
  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname, setOpenMobile]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only a route change closes the tablet overlay; setOpen changes on every toggle
  useEffect(() => {
    if (isOverlay) setOpen(false);
  }, [location.pathname]);

  return (
    <Sidebar id="app-sidebar" collapsible="icon">
      <SidebarHeader>
        <SidebarLogoSection />
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        {/* Overview Section */}
        <SidebarGroup data-section="sidebar-overview">
          <SidebarGroupLabel>Oversikt</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu id="sidebar-nav-overview">
              {!auth.isCustomerUser && <DashboardsSidebarGroup enabled={flagSidebarButtonDashboard} />}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.startsWith("/mission/overview")}
                  tooltip="Turrapporter"
                >
                  <Link id="sidebar-link-missions" to="/mission/overview">
                    <FileLineChart className="h-4 w-4" />
                    <span>Turrapporter</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {!auth.isCustomerUser && (
          <>
            <SidebarSeparator />
            {/* Resources Section */}
            <SidebarGroup data-section="sidebar-resources">
              <SidebarGroupContent>
                <SidebarMenu id="sidebar-nav-resources">
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === "/vessels"} tooltip="Fartøyskart">
                      <Link id="sidebar-link-vessels" to="/vessels">
                        <Ship className="h-4 w-4" />
                        <span>Fartøyskart</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild tooltip="Fleet Tracker">
                      <a
                        id="sidebar-link-fleet-tracker"
                        href={LibFleetTracker.url()}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Radar className="h-4 w-4" />
                        <span className="truncate">Fleet Tracker</span>
                        <ExternalLink className="!size-3 ml-auto text-muted-foreground group-data-[collapsible=icon]:hidden" />
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        {/* Integrations Section - only shown if user has webhook access */}
        {webhookAccess.hasAccess && (
          <>
            <SidebarSeparator />
            <SidebarGroup data-section="sidebar-integrations">
              <SidebarGroupLabel>Integrasjoner</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu id="sidebar-nav-integrations">
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname.startsWith("/settings/webhooks")}
                      tooltip="Webhooks"
                    >
                      <Link id="sidebar-link-webhooks" to="/settings/webhooks">
                        <Webhook className="h-4 w-4" />
                        <span>Webhooks</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}

        <SidebarEmptySpaceToggle />
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <ThemeMenuItem />
          <SidebarMenuItem>
            <SidebarMenuButton
              id="sidebar-feedback-trigger"
              onClick={() => setFeedbackOpen(true)}
              tooltip="Gi tilbakemelding"
              className="border border-gray-300/80 bg-gray-200 transition-colors duration-100 hover:bg-gray-300 active:bg-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:active:bg-gray-600"
            >
              <Lightbulb className="h-4 w-4 text-yellow-500" />
              <span>Gi tilbakemelding</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <UserProfileSection />
      </SidebarFooter>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </Sidebar>
  );
}

// Fills the empty background under the nav groups so clicking it toggles the
// sidebar; the hover tint is the affordance telling the user it is clickable.
function SidebarEmptySpaceToggle() {
  const { state, toggleSidebar, isMobile } = useSidebar();

  if (isMobile) {
    return null;
  }

  const label = state === "collapsed" ? "Utvid sidepanel (⌘B)" : "Minimer sidepanel (⌘B)";

  return (
    <button
      type="button"
      id="sidebar-empty-space-toggle"
      onClick={toggleSidebar}
      title={label}
      aria-label={label}
      tabIndex={-1}
      className="min-h-8 w-full flex-1 cursor-pointer transition-colors duration-100 hover:bg-gray-200/70 dark:hover:bg-white/5"
    />
  );
}

function SidebarLogoSection() {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <div
      data-section="sidebar-logo"
      className="flex items-center justify-between px-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0"
    >
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={isCollapsed ? toggleSidebar : undefined}
          className="m-1 h-10 w-10 shrink-0 group-data-[collapsible=icon]:m-0 group-data-[collapsible=icon]:h-7 group-data-[collapsible=icon]:w-7"
          title={isCollapsed ? "Utvid sidepanel (⌘B)" : undefined}
        >
          <img id="sidebar-logo-img" src="/image38.svg" alt="Frøy logo" className="h-full w-full object-contain" />
        </button>
        <div className="flex flex-col overflow-hidden group-data-[collapsible=icon]:hidden">
          <h2 id="sidebar-brand-title" className="font-semibold text-lg text-sidebar-foreground leading-7">
            Frøy
          </h2>
          <p className="mt-[-5px] text-sidebar-foreground text-sm leading-5">Engage</p>
        </div>
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        className="h-7 w-7 shrink-0 group-data-[collapsible=icon]:hidden"
        title="Minimer sidepanel (⌘B)"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      {/* Otherwise the collapsed rail only expands via the logo or empty space, hard to find on a tablet. */}
      <Button
        id="sidebar-expand"
        variant="ghost"
        size="icon"
        onClick={toggleSidebar}
        className="tap-target relative hidden h-7 w-7 shrink-0 group-data-[collapsible=icon]:flex"
        title="Utvid sidepanel (⌘B)"
        aria-label="Utvid sidepanel"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

function UserProfileSection() {
  const auth = Auth.useAuth();
  const nav = useNavigate();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <div data-section="sidebar-user">
      <Popover>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            id="sidebar-user-trigger"
            size="lg"
            className="border border-gray-300/80 bg-gray-200 transition-colors duration-100 hover:bg-gray-300 active:bg-gray-300 data-[state=open]:bg-gray-300 data-[state=open]:hover:bg-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600 dark:active:bg-gray-600 dark:data-[state=open]:bg-gray-600 dark:data-[state=open]:hover:bg-gray-600"
            tooltip={auth?.user?.name || "Bruker"}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-400">
              <CircleUser className="h-4 w-4 text-sidebar-primary-foreground" />
            </div>
            <div className="flex min-w-0 flex-col items-start">
              <span
                id="sidebar-user-name"
                className="w-full truncate font-semibold text-sidebar-accent-foreground text-sm leading-tight"
              >
                {auth?.user?.name || "<navn mangler>"}
              </span>
              <span id="sidebar-user-email" className="w-full truncate text-muted-foreground text-xs leading-tight">
                {auth?.user?.email}
              </span>
            </div>
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          id="sidebar-user-popover"
          className="w-40"
          side={isCollapsed ? "right" : "top"}
          align="center"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Button
            id="sidebar-logout-btn"
            variant="outline"
            className="w-full"
            onClick={async () => {
              await logout();
              nav({ to: "/login" });
            }}
          >
            Logg ut
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function DashboardsSidebarGroup(props: { enabled: boolean }) {
  const location = useLocation();
  const isOverviewActive = location.pathname === "/dashboard";

  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isOverviewActive} disabled={!props.enabled} tooltip="Dashboard">
        <Link
          id="sidebar-link-dashboard"
          to="/dashboard"
          aria-disabled={!props.enabled || undefined}
          className="text-inherit"
        >
          <LayoutDashboard className="h-4 w-4" />
          <span>Oversikt</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}
